import React, { Children } from 'react';
import { PrivacyHeader } from './PrivacyHeader';
import { PrivacyTOC, TOCItem } from './PrivacyTOC';
import { PrivacyFooter } from './PrivacyFooter';
import { PrivacySection } from './PrivacySection';
import { ContactCard } from './ContactCard';
const sections: TOCItem[] = [{
  id: 'who-we-are',
  number: 1,
  title: 'Who We Are'
}, {
  id: 'our-role',
  number: 2,
  title: 'Our Role Under Privacy Law'
}, {
  id: 'what-we-collect',
  number: 3,
  title: 'Information We Collect'
}, {
  id: 'how-we-use',
  number: 4,
  title: 'How We Use This Information'
}, {
  id: 'legal-basis',
  number: 5,
  title: 'Legal Basis for Processing'
}, {
  id: 'third-party',
  number: 6,
  title: 'Third-Party Services'
}, {
  id: 'webhooks',
  number: 7,
  title: 'Shopify Mandatory GDPR Webhooks'
}, {
  id: 'retention',
  number: 8,
  title: 'Data Retention'
}, {
  id: 'international',
  number: 9,
  title: 'International Data Transfers'
}, {
  id: 'security',
  number: 10,
  title: 'Security'
}, {
  id: 'rights',
  number: 11,
  title: 'Rights of Individuals'
}, {
  id: 'children',
  number: 12,
  title: "Children's Privacy"
}, {
  id: 'changes',
  number: 13,
  title: 'Changes to This Policy'
}, {
  id: 'contact',
  number: 14,
  title: 'Contact Us'
}];
export function PrivacyPolicy() {
  return <div className="w-full min-h-screen bg-cream text-slate" data-id="element-28">
      <PrivacyHeader data-id="element-29" />

      {/* Hero */}
      <section className="bg-cream" data-id="element-30">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 md:pt-24 pb-12 md:pb-16" data-id="element-31">
          <div className="max-w-3xl" data-id="element-32">
            <p className="font-sans text-sm font-semibold text-sky mb-5 uppercase" style={{
            letterSpacing: '0.08em'
          }} data-id="element-33">
              Legal · WinePik App
            </p>
            <h1 className="font-serif text-[40px] md:text-[64px] text-navy leading-[1.1] mb-6" style={{
            letterSpacing: '-0.02em'
          }} data-id="element-34">
              App Privacy <span className="text-sky" data-id="element-35">Policy</span>
            </h1>
            <p className="text-sm text-stone font-medium mb-8" data-id="element-36">
              Last updated: <span className="text-slate" data-id="element-37">13 May 2026</span>
            </p>
            <p className="text-[18px] md:text-[20px] leading-[1.5] text-slate font-medium mb-5" data-id="element-38">
              This Privacy Policy explains how{' '}
              <span className="text-navy font-semibold" data-id="element-39">WinePik Pty Ltd</span>{' '}
              ("WinePik", "we", "us", or "our") collects, uses, stores, and
              discloses personal information when a Shopify merchant installs
              and uses the WinePik app ("the App"), and when a customer of that
              merchant interacts with the App's subscription widget or customer
              portal.
            </p>
            <p className="text-[18px] leading-[1.6] text-slate mb-5" data-id="element-40">
              This policy is separate from our{' '}
              <a href="#" className="text-sky font-medium hover:text-sky-hover hover:underline underline-offset-[3px] transition-colors" data-id="element-41">
                website privacy policy
              </a>
              , which covers visitors to winepik.io.
            </p>
            <p className="text-[18px] leading-[1.6] text-slate" data-id="element-42">
              We are committed to protecting privacy in accordance with
              applicable laws, including the Australian Privacy Act 1988 (Cth),
              the EU General Data Protection Regulation (GDPR), the UK GDPR, the
              California Consumer Privacy Act (CCPA), the New Zealand Privacy
              Act 2020, and South Africa's Protection of Personal Information
              Act (POPIA).
            </p>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-6 lg:px-10" data-id="element-43">
          <div className="h-px bg-slate-light" data-id="element-44" />
        </div>
      </section>

      {/* Main grid */}
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12 md:py-16" data-id="element-45">
        <div className="grid lg:grid-cols-[260px_1fr] xl:grid-cols-[280px_1fr] gap-12 xl:gap-20" data-id="element-46">
          <PrivacyTOC items={sections} data-id="element-47" />

          <main className="max-w-3xl" data-id="element-48">
            <PrivacySection id="who-we-are" number={1} title="Who We Are" data-id="element-49">
              <p data-id="element-50">
                <strong className="text-navy font-semibold" data-id="element-51">
                  WinePik Pty Ltd
                </strong>
              </p>
              <ul data-id="element-52">
                <li data-id="element-53">ABN: 56 694 172 480</li>
                <li data-id="element-54">340 Sandy Creek Rd, Riddells Creek VIC 3431, Australia</li>
                <li data-id="element-55">
                  Email:{' '}
                  <a href="mailto:privacy@winepik.io" data-id="element-56">privacy@winepik.io</a>
                </li>
              </ul>
            </PrivacySection>

            <PrivacySection id="our-role" number={2} title="Our Role Under Privacy Law" data-id="element-57">
              <p data-id="element-58">
                When a merchant installs WinePik on their Shopify store, the
                merchant is the{' '}
                <strong className="text-navy font-semibold" data-id="element-59">
                  data controller
                </strong>{' '}
                of their store and subscriber data. WinePik acts as a{' '}
                <strong className="text-navy font-semibold" data-id="element-60">
                  data processor
                </strong>
                , processing personal information on the merchant's behalf and
                under their instructions in order to provide subscription
                management services.
              </p>
              <p data-id="element-61">
                The merchant is responsible for ensuring they have the lawful
                basis to collect and process their customers' personal
                information and for providing their customers with appropriate
                privacy notices.
              </p>
            </PrivacySection>

            <PrivacySection id="what-we-collect" number={3} title="Information We Collect" data-id="element-62">
              <h3 data-id="element-63">3.1 Information collected through the Shopify API</h3>
              <p data-id="element-64">
                When a merchant installs WinePik, we access the following data
                through the Shopify Admin GraphQL API in order to provide
                subscription management services:
              </p>
              <ul data-id="element-65">
                <li data-id="element-66">
                  <strong className="text-navy font-semibold" data-id="element-67">
                    Shop information:
                  </strong>{' '}
                  shop name, domain, contact email, timezone, currency, and
                  Shopify plan
                </li>
                <li data-id="element-68">
                  <strong className="text-navy font-semibold" data-id="element-69">
                    Products and variants:
                  </strong>{' '}
                  product titles, prices, images, inventory levels, vendor, and
                  selling plan associations
                </li>
                <li data-id="element-70">
                  <strong className="text-navy font-semibold" data-id="element-71">
                    Customers:
                  </strong>{' '}
                  names, email addresses, shipping and billing addresses, order
                  history, tags, and marketing consent status
                </li>
                <li data-id="element-72">
                  <strong className="text-navy font-semibold" data-id="element-73">Orders:</strong>{' '}
                  order details, fulfilment status, line items, and totals
                </li>
                <li data-id="element-74">
                  <strong className="text-navy font-semibold" data-id="element-75">
                    Subscription contracts and selling plans:
                  </strong>{' '}
                  recurring order details, billing cycles, frequencies, and
                  renewal schedules
                </li>
              </ul>

              <h3 data-id="element-76">3.2 Information collected directly from merchants</h3>
              <p data-id="element-77">When a merchant uses the App, we collect:</p>
              <ul data-id="element-78">
                <li data-id="element-79">
                  Account and authentication information (email address, login
                  credentials)
                </li>
                <li data-id="element-80">
                  App configuration settings (club setup, pack sizes,
                  frequencies, discounts, branding, theme preferences)
                </li>
                <li data-id="element-81">
                  Integration credentials when the merchant connects third-party
                  services (for example, a Klaviyo private API key)
                </li>
                <li data-id="element-82">
                  Support communications and bug reports submitted through the
                  in-app feedback widget
                </li>
                <li data-id="element-83">
                  Automated activity logs of merchant actions within the App
                </li>
              </ul>

              <h3 data-id="element-84">
                3.3 Information collected from merchants' customers
                (subscribers)
              </h3>
              <p data-id="element-85">
                When a customer of a WinePik-using merchant interacts with the
                subscription signup widget or the customer portal, we collect:
              </p>
              <ul data-id="element-86">
                <li data-id="element-87">Name and email address</li>
                <li data-id="element-88">Shipping and billing addresses</li>
                <li data-id="element-89">
                  Subscription preferences (pack size, frequency, product
                  selection, delivery dates)
                </li>
                <li data-id="element-90">
                  Payment method tokens — we never store full payment card
                  details; these remain with Shopify Payments or the merchant's
                  payment processor
                </li>
                <li data-id="element-91">Login session data when accessing the subscriber portal</li>
                <li data-id="element-92">Communication and email marketing preferences</li>
              </ul>
            </PrivacySection>

            <PrivacySection id="how-we-use" number={4} title="How We Use This Information" data-id="element-93">
              <p data-id="element-94">We use the information described above only to:</p>
              <ul data-id="element-95">
                <li data-id="element-96">
                  Provide subscription management services to the merchant
                  (creating, renewing, pausing, skipping, cancelling, swapping,
                  and rescheduling subscriptions)
                </li>
                <li data-id="element-97">Process recurring orders through Shopify's checkout</li>
                <li data-id="element-98">
                  Synchronise subscription and lifecycle events to a merchant's
                  connected Klaviyo account, when the merchant has configured
                  this integration
                </li>
                <li data-id="element-99">Generate analytics and reporting for the merchant</li>
                <li data-id="element-100">
                  Authenticate subscribers logging into the subscriber portal
                </li>
                <li data-id="element-101">Provide customer support to the merchant</li>
                <li data-id="element-102">Identify and fix bugs and improve the App</li>
                <li data-id="element-103">Comply with our legal obligations</li>
              </ul>
              <p data-id="element-104">
                We do not use merchant or subscriber data for our own marketing
                purposes, do not sell personal information, and do not share
                data with third parties except as described in section 6.
              </p>
            </PrivacySection>

            <PrivacySection id="legal-basis" number={5} title="Legal Basis for Processing (GDPR / UK GDPR)" data-id="element-105">
              <p data-id="element-106">
                For data processed on behalf of merchants (subscription data and
                subscriber data), WinePik acts as a data processor under the
                merchant's instructions. The merchant is the data controller and
                is responsible for establishing the legal basis for processing.
              </p>
              <p data-id="element-107">
                For data we collect directly from merchants (account
                information, app configuration, support communications), we rely
                on:
              </p>
              <ul data-id="element-108">
                <li data-id="element-109">
                  <strong className="text-navy font-semibold" data-id="element-110">
                    Performance of a contract
                  </strong>{' '}
                  — to provide the App and fulfil our obligations to the
                  merchant
                </li>
                <li data-id="element-111">
                  <strong className="text-navy font-semibold" data-id="element-112">
                    Legitimate interests
                  </strong>{' '}
                  — to operate, secure, and improve the App
                </li>
                <li data-id="element-113">
                  <strong className="text-navy font-semibold" data-id="element-114">
                    Legal obligation
                  </strong>{' '}
                  — where required by applicable law
                </li>
              </ul>
            </PrivacySection>

            <PrivacySection id="third-party" number={6} title="Third-Party Services" data-id="element-115">
              <p data-id="element-116">
                We use the following third-party services to operate the App.
                Each is bound by data processing terms appropriate to its role.
              </p>
              <ul data-id="element-117">
                <li data-id="element-118">
                  <strong className="text-navy font-semibold" data-id="element-119">Shopify</strong>{' '}
                  (United States and other regions) — primary data source.
                  WinePik accesses store, product, customer, order, and
                  subscription contract data through the Shopify Admin API.
                </li>
                <li data-id="element-120">
                  <strong className="text-navy font-semibold" data-id="element-121">Klaviyo</strong>{' '}
                  (United States) — only used when a merchant connects their
                  Klaviyo account. We transmit subscription event data and
                  limited profile attributes using the private API key the
                  merchant supplies.
                </li>
                <li data-id="element-122">
                  <strong className="text-navy font-semibold" data-id="element-123">
                    Auth0, by Okta
                  </strong>{' '}
                  (United States and other regions) — authentication and
                  identity management for merchants and subscribers.
                </li>
                <li data-id="element-124">
                  <strong className="text-navy font-semibold" data-id="element-125">
                    Amazon Web Services
                  </strong>{' '}
                  (Asia Pacific region) — application hosting, database storage,
                  and infrastructure.
                </li>
                <li data-id="element-126">
                  <strong className="text-navy font-semibold" data-id="element-127">
                    Cloudflare
                  </strong>{' '}
                  (United States and global edge network) — content delivery,
                  webhook processing, and security.
                </li>
                <li data-id="element-128">
                  <strong className="text-navy font-semibold" data-id="element-129">Userback</strong>{' '}
                  (Australia) — capturing in-app feedback and bug reports when
                  used by the merchant.
                </li>
              </ul>
              <p data-id="element-130">
                We do not transfer data to any other third parties for their own
                marketing or commercial purposes.
              </p>
            </PrivacySection>

            <PrivacySection id="webhooks" number={7} title="Shopify Mandatory GDPR Webhooks" data-id="element-131">
              <p data-id="element-132">
                WinePik subscribes to and responds to Shopify's three mandatory
                GDPR webhooks:
              </p>
              <ul data-id="element-133">
                <li data-id="element-134">
                  <strong className="text-navy font-semibold" data-id="element-135">
                    customers/data_request
                  </strong>{' '}
                  — when a merchant's customer requests a copy of their personal
                  information held by the App, we respond within 30 days
                </li>
                <li data-id="element-136">
                  <strong className="text-navy font-semibold" data-id="element-137">
                    customers/redact
                  </strong>{' '}
                  — when a merchant requests deletion of a specific customer, we
                  delete that customer's personal information within 30 days
                </li>
                <li data-id="element-138">
                  <strong className="text-navy font-semibold" data-id="element-139">
                    shop/redact
                  </strong>{' '}
                  — sent by Shopify 48 hours after a merchant uninstalls the
                  App; we delete all merchant and subscriber data associated
                  with that store within 30 days of receipt
                </li>
              </ul>
            </PrivacySection>

            <PrivacySection id="retention" number={8} title="Data Retention" data-id="element-140">
              <p data-id="element-141">
                While a merchant has WinePik installed, we retain merchant and
                subscriber data for as long as it is needed to provide the App.
              </p>
              <p data-id="element-142">
                When a merchant uninstalls the App, we delete all merchant and
                subscriber data associated with that store within 30 days of
                receiving Shopify's{' '}
                <strong className="text-navy font-semibold" data-id="element-143">shop/redact</strong>{' '}
                webhook (sent 48 hours post-uninstall).
              </p>
              <p data-id="element-144">
                When a subscriber requests deletion, we delete their personal
                information within 30 days of receiving the{' '}
                <strong className="text-navy font-semibold" data-id="element-145">
                  customers/redact
                </strong>{' '}
                webhook from Shopify.
              </p>
              <p data-id="element-146">
                We may retain limited subscription contract metadata for longer
                where required for legal, accounting, fraud-prevention, or
                dispute-resolution purposes.
              </p>
            </PrivacySection>

            <PrivacySection id="international" number={9} title="International Data Transfers" data-id="element-147">
              <p data-id="element-148">
                WinePik is based in Australia. Personal information may be
                transferred to and processed in countries outside the country of
                residence of the data subject, including Australia, the United
                States, and other regions where our third-party service
                providers operate.
              </p>
              <p data-id="element-149">
                Where data is transferred outside the EEA or UK, we ensure
                appropriate safeguards are in place, including Standard
                Contractual Clauses approved by the European Commission or UK
                Information Commissioner's Office.
              </p>
            </PrivacySection>

            <PrivacySection id="security" number={10} title="Security" data-id="element-150">
              <p data-id="element-151">
                We implement appropriate technical and organisational measures
                to protect personal information against unauthorised access,
                loss, or disclosure. These include:
              </p>
              <ul data-id="element-152">
                <li data-id="element-153">TLS/SSL encryption for all data in transit</li>
                <li data-id="element-154">Encryption at rest for stored data</li>
                <li data-id="element-155">
                  Restricted access controls and authentication for our internal
                  systems
                </li>
                <li data-id="element-156">Regular security reviews and monitoring</li>
                <li data-id="element-157">
                  Secure handling of merchant-supplied credentials (API keys are
                  stored encrypted)
                </li>
              </ul>
              <p data-id="element-158">
                No method of transmission or storage is completely secure, and
                we cannot guarantee absolute security.
              </p>
            </PrivacySection>

            <PrivacySection id="rights" number={11} title="Rights of Individuals" data-id="element-159">
              <p data-id="element-160">
                Subscribers wishing to exercise privacy rights should generally
                contact the merchant they purchased from, as the merchant is the
                data controller. WinePik will assist merchants in responding to
                these requests.
              </p>
              <p data-id="element-161">
                Where you have a direct relationship with WinePik (for example,
                as a merchant), you may have the following rights depending on
                your jurisdiction:
              </p>
              <ul data-id="element-162">
                <li data-id="element-163">
                  The right to access the personal information we hold about you
                </li>
                <li data-id="element-164">The right to correct inaccurate information</li>
                <li data-id="element-165">The right to request deletion of your information</li>
                <li data-id="element-166">The right to data portability</li>
                <li data-id="element-167">The right to restrict or object to processing</li>
                <li data-id="element-168">The right to withdraw consent</li>
                <li data-id="element-169">
                  The right to lodge a complaint with your local supervisory
                  authority
                </li>
              </ul>
              <p data-id="element-170">
                To exercise any of these rights, contact{' '}
                <a href="mailto:privacy@winepik.io" data-id="element-171">privacy@winepik.io</a>. We
                will respond within the timeframe required by applicable law
                (generally 30 days).
              </p>
            </PrivacySection>

            <PrivacySection id="children" number={12} title="Children's Privacy" data-id="element-172">
              <p data-id="element-173">
                WinePik is not directed at individuals under the age of 18. We
                do not knowingly collect personal information from minors.
                Merchants using WinePik are responsible for ensuring their
                subscribers meet any minimum age requirements applicable to the
                sale of alcohol or other products in their jurisdiction.
              </p>
            </PrivacySection>

            <PrivacySection id="changes" number={13} title="Changes to This Policy" data-id="element-174">
              <p data-id="element-175">
                We may update this Privacy Policy from time to time. Material
                changes will be communicated to merchants through the App or by
                email, and reflected in the "Last updated" date at the top of
                this page.
              </p>
            </PrivacySection>

            <PrivacySection id="contact" number={14} title="Contact Us" isLast data-id="element-176">
              <p data-id="element-177">
                For questions, concerns, or requests regarding this Privacy
                Policy or our handling of personal information:
              </p>
              <ContactCard data-id="element-178" />
              <div className="mt-8 space-y-3" data-id="element-179">
                <p data-id="element-180">
                  <strong className="text-navy font-semibold" data-id="element-181">
                    For EEA users:
                  </strong>{' '}
                  you have the right to lodge a complaint with your local data
                  protection authority.
                </p>
                <p data-id="element-182">
                  <strong className="text-navy font-semibold" data-id="element-183">
                    For South African users:
                  </strong>{' '}
                  you may contact the Information Regulator of South Africa at{' '}
                  <a href="https://inforeg.org.za" target="_blank" rel="noopener noreferrer" data-id="element-184">
                    inforeg.org.za
                  </a>
                  .
                </p>
              </div>
            </PrivacySection>
          </main>
        </div>
      </div>

      <PrivacyFooter data-id="element-185" />
    </div>;
}